import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  fetchClientProfile,
  fetchClientCustomers,
  fetchEscalatedConversations,
  markCustomerAsRead,
  fetchClientConversation,
  deleteCustomerConversation,
} from '../services/api';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const [clientProfile, setClientProfile] = useState(null);
  const [customers, setCustomers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('chat_customers')) || []; } catch { return []; }
  });
  const [selectedCustomer, setSelectedCustomer] = useState(() => {
    try { return localStorage.getItem('chat_selected') || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const wsRef = useRef(null);
  const wsDebugConnSeqRef = useRef(0);
  const selectedCustomerRef = useRef(selectedCustomer);
  const profileLoadRef = useRef(false);
  const [conversationsCache, setConversationsCache] = useState({}); // { [customerId]: messages[] }
  const [wsIncomingMsg, setWsIncomingMsg] = useState(null); // Real-time target for incoming messages
  const [wsStatusUpdate, setWsStatusUpdate] = useState(null); // For message status updates { customer_id, wa_message_id, status }
  const [escalations, setEscalations] = useState([]);

  const conversationsCacheRef = useRef(conversationsCache);
  useEffect(() => {
    conversationsCacheRef.current = conversationsCache;
  }, [conversationsCache]);

  const prefetchConversation = useCallback(async (customerId) => {
    if (!customerId) return;
    if (conversationsCacheRef.current[customerId]) return; // already cached
    try {
      const { data } = await fetchClientConversation(customerId);
      setConversationsCache(prev => ({ ...prev, [customerId]: data }));
    } catch (e) {
      // ignore prefetch errors
    }
  }, []); // No dependencies - stable function

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await fetchClientProfile();
      setClientProfile(data);
      
      // Immediately ping backend to confirm frontend is running
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin + '/api/v1';
      const token = localStorage.getItem('token');
      try {
        await fetch(apiUrl + '/ws/client-ping', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            status: 'profile_loaded',
            message: 'Frontend is running and profile loaded successfully',
            clientId: data.id,
            clientName: data.name,
            clientStatus: data.status
          })
        });
      } catch (e) {
        console.error('Failed to ping backend:', e);
      }
    } catch (err) {
      setError('Failed to fetch your profile. Please try logging in again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const { data } = await fetchClientCustomers();
      setCustomers(prev => {
        const currentSelected = selectedCustomerRef.current;
        // Preserve selection if still present; otherwise choose first
        if (!currentSelected && data.length > 0) {
          const nextId = data[0].customer_id;
          selectedCustomerRef.current = nextId;
          setSelectedCustomer(nextId);
          try { localStorage.setItem('chat_selected', nextId); } catch {}
        } else if (currentSelected && !data.some(c => c.customer_id === currentSelected) && data.length > 0) {
          const nextId = data[0].customer_id;
          selectedCustomerRef.current = nextId;
          setSelectedCustomer(nextId);
        }
        try { localStorage.setItem('chat_customers', JSON.stringify(data)); } catch {}
        try { if (selectedCustomerRef.current) localStorage.setItem('chat_selected', selectedCustomerRef.current); } catch {}
        return data;
      });
      // Prefetch conversations for top 1-3 recent customers for instant open
      const top = [...data]
        .sort((a,b) => new Date(b.last_ts || 0) - new Date(a.last_ts || 0))
        .slice(0, 3);
      top.forEach(c => prefetchConversation(c.customer_id));
    } catch (e) {
      console.error('Failed to load customers:', e);
    }
  }, [prefetchConversation]);

  const loadEscalations = useCallback(async () => {
    try {
      const { data } = await fetchEscalatedConversations();
      setEscalations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load escalated conversations:', e);
    }
  }, []);

  const handleCustomerSelect = useCallback(async (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    if (customer && customer.unread_count > 0) {
      try {
        await markCustomerAsRead(customerId);
        setCustomers(prev => prev.map(c => c.customer_id === customerId ? { ...c, unread_count: 0 } : c));
      } catch (error) {
        console.error('Failed to mark customer as read:', error);
      }
    }
  selectedCustomerRef.current = customerId;
  setSelectedCustomer(customerId);
    try { localStorage.setItem('chat_selected', customerId); } catch {}
    // Optimistically prefetch selected conversation
    prefetchConversation(customerId);
  }, [customers, prefetchConversation]);

  const handleNameUpdate = useCallback((customerId, newName) => {
    setCustomers(prev => prev.map(c => c.customer_id === customerId ? { ...c, name: newName } : c));
  }, []);

  const handleDeleteConversation = useCallback(async (customerId) => {
    try {
      await deleteCustomerConversation(customerId);

      let nextSelected = selectedCustomer;
      setCustomers(prev => {
        const filtered = prev.filter(c => c.customer_id !== customerId);
        if (selectedCustomer === customerId) {
          nextSelected = filtered[0]?.customer_id || null;
        }
        try { localStorage.setItem('chat_customers', JSON.stringify(filtered)); } catch {}
        return filtered;
      });

      setConversationsCache(prev => {
        const { [customerId]: _removed, ...rest } = prev;
        return rest;
      });

      if (selectedCustomer === customerId) {
        selectedCustomerRef.current = nextSelected;
        setSelectedCustomer(nextSelected);
        try {
          if (nextSelected) {
            localStorage.setItem('chat_selected', nextSelected);
          } else {
            localStorage.removeItem('chat_selected');
          }
        } catch {}
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      throw error;
    }
  }, [selectedCustomer]);

  useEffect(() => {
    let cancelled = false;

    const evaluateAuth = async () => {
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');

      if (!token || role !== 'client') {
        if (!cancelled) {
          setClientProfile(null);
          setCustomers([]);
          setConversationsCache({});
          setEscalations([]);
          selectedCustomerRef.current = null;
          setSelectedCustomer(null);
          setLoading(false);
        }
        profileLoadRef.current = false;
        return;
      }

      if (profileLoadRef.current) {
        return;
      }

      profileLoadRef.current = true;
      if (!cancelled) {
        setLoading(true);
      }

      try {
        await loadProfile();
      } finally {
        profileLoadRef.current = false;
      }
    };

    evaluateAuth();

    const handleAuthChange = () => {
      evaluateAuth();
    };

    window.addEventListener('auth-token-changed', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);

    return () => {
      cancelled = true;
      window.removeEventListener('auth-token-changed', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [loadProfile]);

  useEffect(() => {
    selectedCustomerRef.current = selectedCustomer;
  }, [selectedCustomer]);

  useEffect(() => {
    console.log('[ChatContext] useEffect triggered - clientProfile:', clientProfile?.id, 'status:', clientProfile?.status);

    if (!clientProfile) {
      console.log('[ChatContext] Skipping WebSocket initialization - profile not yet loaded');
      return;
    }

    const normalizedStatus = (clientProfile.status || '').toLowerCase();
    const isAccountActive = clientProfile.is_active !== false; // treat undefined as active
    if (!isAccountActive || normalizedStatus === 'disconnected') {
      console.log('[ChatContext] Skipping WebSocket initialization - client inactive or explicitly disconnected', {
        isAccountActive,
        normalizedStatus,
      });
      return;
    }

    console.log('[ChatContext] ========== WEBSOCKET INITIALIZATION START ==========');
    console.log('[ChatContext] Client profile:', clientProfile);

    // Initial customers
    loadCustomers();
    loadEscalations();

    // Setup a single persistent WebSocket for the client session
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('[ChatContext] No token found, skipping WebSocket connection');
      return;
    }
    
    // Construct WebSocket URL based on environment
    // In production, REACT_APP_API_URL is https://blastmate-ecbwr.ondigitalocean.app/api/v1
    // We need to connect to /api/v1/ws which will route to the backend service
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin + '/api/v1';
    // #region agent log
    fetch('http://127.0.0.1:7813/ingest/09c3a12d-3025-469f-96cf-0d890faa877f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'477b4c'},body:JSON.stringify({sessionId:'477b4c',runId:'pre-fix',hypothesisId:'H1',location:'frontend/src/context/ChatContext.jsx:269',message:'ws_init_effect',data:{clientId:clientProfile.id||null,profileStatus:clientProfile.status||null,hasToken:!!token,hasExistingSocket:!!wsRef.current,socketReadyState:wsRef.current?.readyState??null,apiUrl},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    console.log('[ChatContext] Raw API URL from env:', process.env.REACT_APP_API_URL);
    console.log('[ChatContext] Using API URL:', apiUrl);
    
    // Convert http(s):// to ws(s):// and append /ws
    const wsUrl = apiUrl
      .replace(/^https:/, 'wss:')
      .replace(/^http:/, 'ws:') + `/ws?token=${encodeURIComponent(token)}`;
    
    console.log('[ChatContext] ==========================================');
    console.log('[ChatContext] Final WebSocket URL:', wsUrl);
    console.log('[ChatContext] Token (first 20 chars):', token.substring(0, 20) + '...');
    console.log('[ChatContext] ==========================================');
    console.log('[ChatContext] Attempting WebSocket connection...');
    
    // Helper function to report status to backend
    const reportToBackend = async (status, extraData = {}) => {
      try {
        const response = await fetch(apiUrl.replace('/ws', '') + '/ws/client-ping', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            status,
            wsUrl,
            hasToken: !!token,
            ...extraData
          })
        });
        if (!response.ok) {
          // #region agent log
          fetch('http://127.0.0.1:7813/ingest/09c3a12d-3025-469f-96cf-0d890faa877f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'477b4c'},body:JSON.stringify({sessionId:'477b4c',runId:'pre-fix',hypothesisId:'H4',location:'frontend/src/context/ChatContext.jsx:300',message:'ws_client_ping_failed',data:{statusLabel:status,httpStatus:response.status,apiUrl},timestamp:Date.now()})}).catch(()=>{});
          // #endregion
        }
      } catch (e) {
        // Silently fail if backend not reachable
      }
    };

    let reconnectTimer = null;
    let closedByEffect = false;

    const scheduleReconnect = (delayMs) => {
      if (closedByEffect) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        console.log('[ChatContext] 🔄 Attempting WebSocket reconnect...');
        openWebSocket();
      }, Math.max(2000, delayMs));
    };

    const openWebSocket = () => {
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        console.log('[ChatContext] WebSocket already open/connecting, skipping new connection');
        return;
      }

      let ws;
      try {
        ws = new WebSocket(wsUrl);
        const debugConnId = ++wsDebugConnSeqRef.current;
        ws.__debugConnId = debugConnId;
        wsRef.current = ws;
        console.log('[ChatContext] WebSocket object created, waiting for connection...');

        // Report connection attempt to backend
        reportToBackend('attempting', { readyState: ws.readyState });
      } catch (error) {
        console.error('[ChatContext] FATAL: Failed to create WebSocket:', error);
        reportToBackend('creation_failed', { error: error.message });
        scheduleReconnect(5000);
        return;
      }

      ws.onopen = () => {
        console.log('[ChatContext] ✅ WebSocket connected successfully!');
        console.log('[ChatContext] WebSocket readyState:', ws.readyState);

        // Report success to backend
        reportToBackend('connected', { readyState: ws.readyState });

        // Keep-alive ping every 30s to prevent DigitalOcean's 60s idle timeout
        ws.pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: 'ping', data: { ts: Date.now() } }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.event === 'new_message' || message.event === 'message_status_update') {
          // #region agent log
          fetch('http://127.0.0.1:7813/ingest/09c3a12d-3025-469f-96cf-0d890faa877f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'477b4c'},body:JSON.stringify({sessionId:'477b4c',runId:'pre-fix',hypothesisId:'H1',location:'frontend/src/context/ChatContext.jsx:358',message:'ws_event_received',data:{connectionId:ws.__debugConnId||null,event:message.event,customerId:message.data?.customer_id||null,waMessageId:message.data?.wa_message_id||null,direction:message.data?.direction||null},timestamp:Date.now()})}).catch(()=>{});
          // #endregion
        }
        console.log('[ChatContext] WebSocket message received:', message);
        
        if (message.event === 'new_customer') {
          loadCustomers();
          if (!selectedCustomerRef.current) {
            setSelectedCustomer(message.data.customer_id);
          }
        } else if (message.event === 'new_message') {
          const customerId = message.data.customer_id;
          const messageText = message.data.text;
          const messageTimestamp = message.data.timestamp;
          const messageDirection = message.data.direction || 'in';
          const isIncoming = messageDirection === 'in';
          const activeCustomer = selectedCustomerRef.current;
          
          // Update customer list, and create an entry if it does not exist yet
          setCustomers(prev => {
            let found = false;
            const updated = prev.map(c => {
              if (c.customer_id === customerId) {
                found = true;
                return {
                  ...c,
                  unread_count: isIncoming && customerId !== activeCustomer
                    ? (c.unread_count || 0) + 1
                    : c.unread_count || 0,
                  last_message: messageText,
                  last_ts: messageTimestamp,
                };
              }
              return c;
            });

            if (!found) {
              loadCustomers();
              return [
                ...updated,
                {
                  customer_id: customerId,
                  name: null,
                  last_message: messageText,
                  last_ts: messageTimestamp,
                  unread_count: isIncoming ? 1 : 0,
                },
              ].sort((a, b) => new Date(b.last_ts || 0) - new Date(a.last_ts || 0));
            }

            return updated.sort((a, b) => new Date(b.last_ts || 0) - new Date(a.last_ts || 0));
          });
          
          // IMPORTANT: Only trigger refresh for INCOMING messages (direction: 'in')
          // When YOU send a message (direction: 'out'), the optimistic UI already handles it
          // Triggering a reload for outgoing messages causes jerk/jiggle
          if (isIncoming) {
            // Insert message directly into cache for instant display (no server round-trip)
            setConversationsCache(prev => {
              const existing = prev[customerId] || [];
              const newMsg = {
                id: message.data.wa_message_id || `ws-${Date.now()}`,
                direction: 'in',
                text: messageText,
                ts: messageTimestamp,
                status: 'received',
                wa_message_id: message.data.wa_message_id,
                template_name: message.data.template_name,
                params: message.data.params,
              };
              // Avoid duplicates by checking wa_message_id
              if (newMsg.wa_message_id && existing.some(m => m.wa_message_id === newMsg.wa_message_id)) {
                return prev;
              }
              return { ...prev, [customerId]: [...existing, newMsg] };
            });
            setWsIncomingMsg({ customerId, timestamp: Date.now() });
            console.log('[ChatContext] ✅ Incoming message - inserted into cache for customer:', customerId);
            
            // ⭐ REAL-TIME READ RECEIPT FIX ⭐
            // If the user already has THIS customer's chat open on the screen,
            // we must instantly tell the backend we "read" it, so the sender gets blue ticks.
            if (activeCustomer === customerId) {
              console.log('[ChatContext] 👁️ Chat is currently open! Instantly sending read receipt for:', customerId);
              // Fire and forget to avoid blocking UI
              markCustomerAsRead(customerId).catch(err => 
                console.error('[ChatContext] Failed to send real-time read receipt:', err)
              );
            }
            } else {
              // Outgoing message: update cache immediately so drafts stay visible
              setConversationsCache(prev => {
                const existing = prev[customerId] || [];
                
                // 1. Exact match by wa_message_id
                let targetIndex = -1;
                if (message.data.wa_message_id) {
                  targetIndex = existing.findIndex(msg => msg.wa_message_id === message.data.wa_message_id);
                }
                
                // 2. Fallback: Identify optimistic message by text (even if it already received wa_message_id from status)
                if (targetIndex === -1) {
                  for (let i = existing.length - 1; i >= 0; i--) {
                    const m = existing[i];
                    // Strict deduplication by text and direction. We don't check !m.wa_message_id 
                    // because the status webhook might have already backfilled it!
                    if (m.direction === 'out' && m.text === messageText) {
                      targetIndex = i;
                      break;
                    }
                  }
                }
                
                if (targetIndex !== -1) {
                  const updated = [...existing];
                  updated[targetIndex] = {
                    ...updated[targetIndex],
                    status: message.data.status || updated[targetIndex].status,
                    wa_message_id: message.data.wa_message_id || updated[targetIndex].wa_message_id,
                    template_name: message.data.template_name || updated[targetIndex].template_name,
                    params: message.data.params || updated[targetIndex].params,
                  };
                  return { ...prev, [customerId]: updated };
                } else {
                  // Sent from another session or bot: append cleanly
                  const newMsg = {
                    id: message.data.wa_message_id || `ws-${Date.now()}`,
                    direction: 'out',
                    text: messageText,
                    ts: messageTimestamp,
                    status: message.data.status || 'sent',
                    wa_message_id: message.data.wa_message_id,
                    template_name: message.data.template_name,
                    params: message.data.params,
                  };
                  return { ...prev, [customerId]: [...existing, newMsg] };
                }
              });
              console.log('[ChatContext] 🚫 Outgoing message - cache updated, optimistic UI handled');
            }
          
          
        } else if (message.event === 'escalation') {
          const escalation = message.data || {};
          setEscalations(prev => {
            const next = prev.filter(item => item.customer_id !== escalation.customer_id);
            return [
              ...next,
              {
                customer_id: escalation.customer_id,
                customer_name: escalation.customer_name,
                original_message: escalation.original_message,
                escalated_at: escalation.escalated_at,
              },
            ].sort((a, b) => new Date(b.escalated_at || 0) - new Date(a.escalated_at || 0));
          });
        } else if (message.event === 'escalation_resolved') {
          const customerId = message.data?.customer_id;
          if (customerId) {
            setEscalations(prev => prev.filter(item => item.customer_id !== customerId));
          }
        } else if (message.event === 'message_status_update') {
          // Don't trigger full reload - just pass the status update to ChatWindow
          // This allows smooth status updates (✓ → ✓✓ → ✓✓ blue) without jerk
          console.log('[ChatContext] 📊 Status update received:', message.data);
          setWsStatusUpdate({
            customer_id: message.data.customer_id,
            wa_message_id: message.data.wa_message_id,
            status: message.data.status,
            timestamp: Date.now() // To ensure effect triggers even for same status
          });

          // Also update conversations cache so status persists across customer switches
          const statusCustomerId = message.data.customer_id;
          if (statusCustomerId) {
            setConversationsCache(prev => {
              const existing = Array.isArray(prev[statusCustomerId]) ? prev[statusCustomerId] : [];
              if (existing.length === 0) return prev;
              
              let targetIndex = existing.findIndex(m => m.wa_message_id && m.wa_message_id === message.data.wa_message_id);
              
              // Fallback: match LAST outgoing message without wa_message_id
              if (targetIndex === -1) {
                for (let i = existing.length - 1; i >= 0; i--) {
                  const m = existing[i];
                  if (!m.wa_message_id && m.direction === 'out' && (m.status === 'sending' || m.status === 'sent')) {
                    targetIndex = i;
                    break;
                  }
                }
              }
              
              if (targetIndex === -1) return prev;
              
              const updated = [...existing];
              const msg = updated[targetIndex];
              updated[targetIndex] = {
                ...msg,
                status: message.data.status,
                wa_message_id: message.data.wa_message_id || msg.wa_message_id,
              };
              
              return { ...prev, [statusCustomerId]: updated };
            });
          }
        }
      } catch (error) {
        console.error('[ChatContext] Error parsing WebSocket message:', error);
      }
    };

      ws.onerror = (error) => {
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        console.error('[ChatContext] ❌ WebSocket error:', error);
        console.error('[ChatContext] WebSocket URL was:', wsUrl);
        console.error('[ChatContext] WebSocket readyState:', ws?.readyState);
        console.error('[ChatContext] 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED');

        // Report error to backend
        reportToBackend('error', {
          readyState: ws?.readyState,
          errorType: error?.type || 'unknown'
        });
      };

      ws.onclose = (event) => {
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        console.log('[ChatContext] ❌ WebSocket closed.');
        console.log('[ChatContext] Close code:', event.code);
        console.log('[ChatContext] Close reason:', event.reason);
        console.log('[ChatContext] Was clean:', event.wasClean);
        console.log('[ChatContext] Common codes: 1000=Normal, 1006=Abnormal, 1008=Policy, 1011=Server Error');

        wsRef.current = null;

        // Report close to backend
        reportToBackend('closed', {
          closeCode: event.code,
          closeReason: event.reason || 'none',
          wasClean: event.wasClean
        });

        if (!closedByEffect) {
          const delay = event.wasClean && event.code === 1000 ? 6000 : 3500;
          scheduleReconnect(delay);
        }
      };
    };

    openWebSocket();

    return () => {
      console.log('[ChatContext] Cleaning up WebSocket connection');
      closedByEffect = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try {
          ws.close();
        } catch (closeError) {
          console.warn('[ChatContext] Error while closing WebSocket during cleanup:', closeError);
        }
      }
    };
  }, [clientProfile, loadCustomers, loadEscalations]);

  const setConversation = useCallback((customerId, messagesOrUpdater) => {
    setConversationsCache(prev => {
      const next = typeof messagesOrUpdater === 'function'
        ? messagesOrUpdater(Array.isArray(prev[customerId]) ? prev[customerId] : [])
        : messagesOrUpdater;
      return { ...prev, [customerId]: next };
    });
  }, []);

  const getConversation = useCallback((customerId) => {
    return conversationsCacheRef.current[customerId] || null;
  }, []); // No dependencies - stable function

  const value = {
    clientProfile,
    customers,
    selectedCustomer,
    setSelectedCustomer,
    handleCustomerSelect,
    handleNameUpdate,
    handleDeleteConversation,
    reloadProfile: loadProfile,
    reloadCustomers: loadCustomers,
    reloadEscalations: loadEscalations,
    getConversation,
    setConversation,
    prefetchConversation,
    wsIncomingMsg,
    wsStatusUpdate, // Add status update to context
    escalations,
    loading,
    error,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within a ChatProvider');
  return ctx;
}
